import { queries } from "@/graphql";
import { useQuery } from "@apollo/client/react";

type SystemUser = {
  id: string;
  name: string;
  email: string;
  countryCode: string;
  timezone: string;
  roles: string[];
  lang: string;
  companyId: string;
  partner?: { name?: string } | null;
  status: string;
  isVerified?: boolean;
  lastLogin: string;
  createdAt: string;
  updatedAt: string;
};

/**
 * Custom hook to fetch and format the system users list.
 * @param {string} [searchString] - The search string to filter users.
 * @param {string} [statusFilter] - The status filter to apply.
 * @returns {{usersList: Array<{id: string, name: string, email: string, countryCode: string, timezone: string, roles: Array<string>, lang: string, companyId: string, partner: any, partnerName: string}>, loading: boolean}} - The formatted system users list and loading state.
 */
export function useSystemUsersList(searchString = "", statusFilter = "") {
  const { data, loading } = useQuery<any>(queries.getSystemUsersList, {
    variables: {
      searchString,
      statusFilter,
    },
  });

  // Extracting the necessary data and formatting it as needed
  const formattedData =
    data?.systemUsers.map((user: SystemUser) => ({
      id: user.id,
      name: user.name,
      email: user.email,
      countryCode: user.countryCode,
      timezone: user.timezone,
      roles: user.roles,
      lang: user.lang,
      companyId: user.companyId,
      partner: user.partner,
      status: user.status,
      isVerified: Boolean(user.isVerified),
      lastLogin: user.lastLogin,
      createdAt: user.createdAt,
      updatedAt: user.updatedAt,
      partnerName: user.partner ? user.partner.name : "N/A", // Accessing partner's name
    })) || [];

  return {
    usersList: formattedData,
    loading,
  };
}
